import type { FormInstance } from "antd";
import { Button, Flex, Form } from "antd";
import type { ReactNode } from "react";
import EnterpriseDrawer from "../enterprise-drawer";
import type { DrawerSize } from "../enterprise-drawer/drawerSize";

export interface DrawerFormProps<T extends object> {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: T) => Promise<void> | void;
  title: ReactNode;
  size?: DrawerSize;
  copyUrl?: string;
  copyUrlTooltip?: ReactNode;
  form?: FormInstance<T>;
  initialValues?: Partial<T>;
  submitText?: string;
  cancelText?: string;
  resetText?: string;
  showReset?: boolean;
  onReset?: () => void;
  loading?: boolean;
  disabled?: boolean;
  destroyOnHidden?: boolean;
  children: ReactNode;
}

const DrawerForm = <T extends object>({
  open,
  onClose,
  onSubmit,
  title,
  size = "middle",
  copyUrl,
  copyUrlTooltip,
  form,
  initialValues,
  submitText = "Сохранить",
  cancelText = "Отменить",
  resetText = "Сбросить",
  showReset = false,
  onReset,
  loading,
  disabled,
  destroyOnHidden = true,
  children,
}: DrawerFormProps<T>) => {
  const [internalForm] = Form.useForm<T>();
  const activeForm = form ?? internalForm;
  const submitDisabled = Boolean(disabled || loading);

  const handleReset = () => {
    if (onReset) {
      onReset();
      return;
    }
    activeForm.resetFields();
  };

  return (
    <EnterpriseDrawer
      open={open}
      onClose={onClose}
      title={title}
      size={size}
      copyUrl={copyUrl}
      copyUrlTooltip={copyUrlTooltip}
      destroyOnHidden={destroyOnHidden}
      actions={
        <Flex gap={8}>
          {showReset ? (
            <Button onClick={handleReset} disabled={loading}>
              {resetText}
            </Button>
          ) : null}
          <Button onClick={onClose}>{cancelText}</Button>
          <Button
            type="primary"
            onClick={() => activeForm.submit()}
            loading={loading}
            disabled={submitDisabled}
          >
            {submitText}
          </Button>
        </Flex>
      }
    >
      <Form<T>
        form={activeForm}
        layout="vertical"
        preserve={false}
        initialValues={initialValues}
        onFinish={onSubmit}
      >
        {children}
      </Form>
    </EnterpriseDrawer>
  );
};

export default DrawerForm;
